import { Home, Wrench, MapPin, MessageCircle, ShoppingBag, User } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/contexts/LocaleContext";

export const MobileNav = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { isRTL } = useLocale();

  const navItems = [
    { icon: Home, labelKey: "mobile_nav.home", label: "בית", href: "/" },
    { icon: ShoppingBag, labelKey: "mobile_nav.shop", label: "חנות", href: "/services" },
    { icon: Wrench, labelKey: "mobile_nav.tools", label: "כלים", href: "/tools" },
    { icon: MessageCircle, labelKey: "mobile_nav.community", label: "קהילה", href: "https://chat.whatsapp.com/secretgroup", external: true },
    { icon: User, labelKey: "mobile_nav.profile", label: "אני", href: "/profile" },
  ];

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
      dir={isRTL ? "rtl" : "ltr"}
      role="navigation"
      aria-label="Mobile navigation"
    >
      {/* Glassmorphism background */}
      <div className="absolute inset-0 bg-background/70 backdrop-blur-xl border-t border-white/20 shadow-[0_-8px_32px_rgba(0,0,0,0.12)]" />
      
      {/* Safe area padding for devices with home indicator */}
      <div className="relative flex items-center justify-around h-16 pb-safe">
        {navItems.map((item) => {
          const isActive = !item.external && (
            location.pathname === item.href || 
            (item.href !== "/" && location.pathname.startsWith(item.href))
          );

          const content = (
            <>
              <div className={cn(
                "p-2 rounded-2xl transition-all duration-300",
                isActive && "bg-primary/15 shadow-[0_0_20px_rgba(0,102,204,0.3)]"
              )}>
                <item.icon className={cn(
                  "h-5 w-5 transition-all duration-300",
                  isActive ? "text-primary scale-110" : "text-muted-foreground"
                )} aria-hidden="true" />
              </div>
              <span className={cn(
                "text-[10px] transition-all duration-300",
                isActive ? "font-bold text-primary" : "font-medium text-muted-foreground"
              )}>
                {t(item.labelKey, item.label)}
              </span>
            </>
          );

          if (item.external) {
            return (
              <a
                key={item.href}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center justify-center gap-0.5 px-3 py-2 min-w-[64px] min-h-[44px] touch-target"
              >
                {content}
              </a>
            );
          }

          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 px-3 py-2 min-w-[64px] min-h-[44px] touch-target transition-all duration-300",
                isActive && "scale-105"
              )}
              aria-current={isActive ? "page" : undefined}
            >
              {content}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
